<template>
  <span
    v-element-hover="onHover"
    :class="['hover-to-copy', { select: isSelect }]"
    @click="handleClipboard"
    @mouseup="onMouseUp"
  >
    <base-tooltip
      :label="$t('COPY.CLIPBOARD')"
      :visible="isSelect && !copied"
      position="center"
      side="top"
      dark
    >
      <span ref="id">
        <slot />
      </span>
    </base-tooltip>
  </span>
</template>

<script>
import { useClipboard, useTextSelection } from '@vueuse/core';
import { vElementHover as elementHover } from '@vueuse/components';
import BaseTooltip from '@ui-kit/components/tooltips/BaseTooltip.vue';

export default {
  components: {
    BaseTooltip,
  },
  directives: {
    elementHover
  },
  props: {
    textToCopy: String,
  },
  setup() {
    const { copy } = useClipboard({ legacy: true, navigator: false });
    const { text } = useTextSelection();
    return { copy, textSelection: text };
  },
  data: () => ({
    isHovered: false,
    isSelect: false,
    copied: false,
    clipboardCallback: () => {}
  }),
  methods: {
    handleClipboard() {
      const ignoreCopy = this.diffSelection() !== false;
      const textToCopy = this.textToCopy ?? this.textContent;
      if (ignoreCopy) return;

      this.copied = true;
      this.copy(textToCopy);
      this.$emit('on-copy', textToCopy);
      if (this.isHovered) this.selectText();

      clearTimeout(this.clipboardCallback);
      this.clipboardCallback = setTimeout(() => {
        this.copied = false;
        if (!this.isHovered) this.clearSelection();
      }, 1000);
    },
    selectText() {
      const range = document.createRange();
      range.selectNodeContents(this.$refs.id);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      this.isSelect = true;
    },
    clearSelection() {
      const selection = window.getSelection();
      selection.removeAllRanges();
      this.$nextTick(() => {
        this.isSelect = false;
      });
    },
    onHover(isHovered) {
      this.isHovered = isHovered;

      if (this.diffSelection()) return;

      if (isHovered) this.selectText();
      else if (!this.copied) this.clearSelection();
    },
    onMouseUp() {
      this.isSelect = !this.diffSelection();
    },
    diffSelection() {
      if (!this.textSelection) return null;
      return this.textSelection.trim() !== this.textContent;
    },
  },
  computed: {
    textContent() {
      return this.$refs.id.textContent?.trim();
    }
  },
  beforeUnmount() {
    clearTimeout(this.clipboardCallback);
  },
};
</script>

<style lang="scss" scoped>
.hover-to-copy.select {
  cursor: pointer;
  @include selection {
    color: $secondary-color-dark;
    background: $input-disabled-wuk;
  }
}
</style>
